/**
 * @fileoverview Serviço responsável por gerenciar todas as requisições HTTP para a API do BigCupom
 * @module ApiService
 * @category ApiService
 * @requires axios
 * @requires jwt
 * @requires LocalStorageService
 */

/**
 * Classe que fornece métodos para interagir com a API do BigCupom
 * @class ServiceApi
 * @classdesc Gerencia todas as requisições HTTP, autenticação e tratamento de respostas da API
 */

/**
 * @typedef {Object} ApiRoutes
 * @property {string} signin - Rota para autenticação
 * @property {string} signinCode - Rota para verificação de código
 * @property {string} me - Rota para obter dados do usuário logado
 * @property {string} resendCode - Rota para reenvio de código
 * @property {string} remainingAttempsCode - Rota para obter tentativas restantes de código
 * @property {string} listUsers - Rota para listar usuários
 * @property {string} getUsersId - Rota para obter usuário por ID
 * @property {string} validCpf - Rota para validar CPF
 * @property {string} updateUsersId - Rota para atualizar usuário por ID
 * @property {string} removeUserById - Rota para remover usuário por ID
 * @property {string} updateUserStatusMission - Rota para atualizar status de missão do usuário
 * @property {string} getSmsByUserId - Rota para obter SMS por ID de usuário
 * @property {string} getBannerImages - Rota para obter todas as imagens de banner
 * @property {string} newBannerImage - Rota para criar nova imagem de banner
 * @property {string} orderCategory - Rota para ordenar categorias
 * @property {string} getCarouselImages - Rota para obter imagens do carrossel
 * @property {string} newCarouselImage - Rota para criar nova imagem de carrossel
 * @property {string} uploadImageCarousel - Rota para upload de imagem do carrossel
 * @property {string} orderCarousel - Rota para ordenar imagens do carrossel
 * @property {string} getCarouselId - Rota para obter carrossel por ID
 * @property {string} updateCarousel - Rota para atualizar carrossel por ID
 * @property {string} getPrize - Rota para obter prêmios
 * @property {string} PrizeById - Rota para obter prêmio por ID
 * @property {string} getStores - Rota para obter todas as lojas
 * @property {string} getStoresList - Rota para obter lista otimizada de lojas (id e nome)
 * @property {string} storeDelete - Rota para deletar loja por ID
 * @property {string} uploadImageStore - Rota para upload de imagem da loja
 * @property {string} getStoreId - Rota para obter loja por ID
 * @property {string} updateStore - Rota para atualizar loja por ID
 * @property {string} newStore - Rota para criar nova loja
 * @property {string} getCoupons - Rota para obter cupons
 * @property {string} getCouponsId - Rota para obter cupons por ID
 * @property {string} newCoupon - Rota para criar novo cupom
 * @property {string} updateCoupon - Rota para atualizar cupom por ID
 * @property {string} getCategory - Rota para obter categorias
 * @property {string} getCategoryId - Rota para obter categoria por ID
 * @property {string} newCategory - Rota para criar nova categoria
 * @property {string} updateCategory - Rota para atualizar categoria por ID
 * @property {string} getLandingPage - Rota para obter todas as páginas de destino
 * @property {string} newLandingPage - Rota para criar nova página de destino
 * @property {string} uploadImageLandingPage - Rota para upload de imagem da página de destino
 * @property {string} updateLandingPage - Rota para atualizar página de destino por ID
 * @property {string} deleteLandingPage - Rota para deletar página de destino por ID
 * @property {string} getLandingPageById - Rota para obter página de destino por ID
 * @property {string} getNotifications - Rota para obter todas as notificações
 * @property {string} updateNotificationStatusById - Rota para atualizar status de notificação por ID
 * @property {string} updateAllNotificationStatus - Rota para atualizar todas as notificações
 * @property {string} getAffiliates - Rota para obter todos os afiliados
 * @property {string} newAffiliate - Rota para criar novo afiliado
 * @property {string} updateAffiliate - Rota para atualizar afiliado por ID
 * @property {string} affiliateActive - Rota para ativar afiliado por ID
 * @property {string} getRegisters - Rota para obter registros
 * @property {string} getReportsLogin - Rota para obter relatórios de login
 * @property {string} getReportsReward - Rota para obter relatórios de recompensas
 * @property {string} getAllReviewCategory - Rota para obter todas as categorias de revisão
 * @property {string} getAllReviewCategoryList - Rota para obter lista de todas as categorias de revisão
 * @property {string} newReviewCategory - Rota para criar nova categoria de revisão
 * @property {string} setReviewCategory - Rota para atualizar categoria de revisão por ID
 * @property {string} deleteReviewCategory - Rota para deletar categoria de revisão por ID
 * @property {string} deleteQuestionReviewCategory - Rota para deletar pergunta de categoria de revisão por ID
 * @property {string} deleteQuestionReviewCategoryId - Rota para deletar todas as perguntas de categoria de revisão
 * @property {string} getReviewCategoryById - Rota para obter categoria de revisão por ID
 * @property {string} getQuestionsByCategory - Rota para obter perguntas por categoria
 * @property {string} newQuestionsByCategory - Rota para criar novas perguntas por categoria
 * @property {string} setQuestionsByCategory - Rota para atualizar perguntas por categoria
 * @property {string} setQuestionByQuestionId - Rota para atualizar pergunta por ID
 * @property {string} uploadImageCategory - Rota para upload de imagem da categoria
 * @property {string} findProductsMLList - Rota para encontrar produtos na ML
 * @property {string} getProductsList - Rota para obter lista de produtos
 * @property {string} newProductReview - Rota para criar nova avaliação de produto
 * @property {string} updateProductReview - Rota para atualizar avaliação de produto por ID
 * @property {string} getProductById - Rota para obter produto por ID
 * @property {string} uploadProductImage - Rota para upload de imagem do produto
 * @property {string} getReviewList - Rota para obter lista de avaliações
 * @property {string} getReviewById - Rota para obter avaliação por ID
 * @property {string} setReviewById - Rota para atualizar avaliação por ID
 * @property {string} getAllBrandReview - Rota para obter todas as avaliações de marca
 * @property {string} newBrandReview - Rota para criar nova avaliação de marca
 * @property {string} setBrandReview - Rota para atualizar avaliação de marca por ID
 * @property {string} getBrandById - Rota para obter marca por ID
 * @property {string} getUsersAdmin - Rota para obter todos os usuários administradores
 * @property {string} getusersAdminId - Rota para obter usuário administrador por ID
 * @property {string} updateuserAdminId - Rota para atualizar usuário administrador por ID
 * @property {string} newusersAdmin - Rota para criar novo usuário administrador
 * @property {string} setPassword - Rota para alterar senha do usuário
 * @property {string} setadminUser - Rota para atualizar dados do usuário
 * @property {string} setadminUserActive - Rota para ativar usuário administrador por ID
 * @property {string} getallGroups - Rota para obter todos os grupos
 * @property {string} getGroupId - Rota para obter grupo por ID
 * @property {string} groupActive - Rota para ativar grupo por ID
 * @property {string} newGroup - Rota para criar novo grupo
 * @property {string} updateGroupById - Rota para atualizar grupo por ID
 * @property {string} getallModules - Rota para obter todos os módulos
 * @property {string} newModule - Rota para criar novo módulo
 * @property {string} updateModule - Rota para atualizar módulo por ID
 * @property {string} activeModule - Rota para ativar módulo por ID
 * @property {string} getModuleId - Rota para obter módulo por ID
 * @property {string} orderModules - Rota para ordenar módulos
 * @property {string} getMenus - Rota para obter menus
 * @property {string} getGroupActives - Rota para obter grupos ativos
 * @property {string} generateCoupon - Rota para gerar cupons
 * @property {string} statusGenerateCoupons - Rota para obter status de geração de cupons
 * @property {string} generateAllCoupon - Rota para gerar todos os cupons
 * @property {string} sendPreCoupon - Rota para enviar pré-cupom por ID
 * @property {string} getPreCouponId - Rota para obter pré-cupom por ID
 * @property {string} uploadImageCoupon - Rota para upload de imagem do cupom
 * @property {string} deletePreCouponId - Rota para deletar pré-cupom por ID
 * @property {string} sendPreCouponRekuten - Rota para enviar pré-cupom para Rakuten
 * @property {string} syncPreCouponRekuten - Rota para sincronizar pré-cupom com Rakuten
 * @property {string} getPreCouponRekuten - Rota para obter todos os pré-coupons da Rakuten
 * @property {string} activePreCouponRekuten - Rota para ativar pré-cupom da Rakuten por ID
 * @property {string} getPreCouponByIdRekuten - Rota para obter pré-cupom da Rakuten por ID
 * @property {string} information - Rota para obter informações do dashboard
 * @property {string} getUsersAdminId - Rota para obter usuário administrador por ID
 * @property {string} getRewards - Rota para obter todas as recompensas
 * @property {string} newRewards - Rota para criar nova recompensa
 * @property {string} uploadImageRewards - Rota para upload de imagem de recompensa
 * @property {string} uploadImageProduct - Rota para upload de imagem de produto da recompensa
 * @property {string} getRewardId - Rota para obter recompensa por ID
 * @property {string} updateRewardId - Rota para atualizar recompensa por ID
 * @property {string} getRewardsMinu - Rota para obter todas as recompensas da API
 * @property {string} generateGiftCard - Rota para gerar arquivo de voucher
 * @property {string} getGiftCard - Rota para obter vouchers
 * @property {string} getGenerateGiftCardById - Rota para obter voucher por ID
 * @property {string} getStatusGenerateGiftCard - Rota para obter status de geração de voucher
 * @property {string} getReportsReward - Rota para obter relatórios de recompensas
 * @property {string} getReportsRewardData - Rota para obter dados de relatórios de recompensas
 * @property {string} getReportsAffiliatesLogin - Rota para obter relatórios de login de afiliados
 * @property {string} updatemissionId - Rota para atualizar missão por ID
 * @property {string} newMission - Rota para criar nova missão
 * @property {string} getMissions - Rota para obter todas as missões
 * @property {string} getMissionsDependecy - Rota para obter missões dependentes
 * @property {string} getOrderMissions - Rota para obter lista de ordens de missões
 * @property {string} uploadImageMissions - Rota para upload de imagem de missão
 * @property {string} missionActive - Rota para ativar missão por ID
 * @property {string} orderMission - Rota para ordenar missões
 * @property {string} replacement - Rota para obter todas as trocas
 * @property {string} updateReplacementStatus - Rota para atualizar status de troca por ID
 * @property {string} getReplacementDetailsById - Rota para obter detalhes de troca por ID
 * @property {string} getNotifications - Rota para obter todas as notificações
 * @property {string} updateNotificationStatusById - Rota para atualizar status de notificação por ID
 * @property {string} updateAllNotificationStatus - Rota para atualizar todas as notificações
 * @property {string} listHotOffers - Rota para listar ofertas quentes
 * @property {string} createHotOffers - Rota para criar ofertas quentes
 * @property {string} updateHotOffers - Rota para atualizar ofertas quentes por ID
 * @property {string} getPostback - Rota para obter postbacks
 * @property {string} createPostback - Rota para criar postback
 * @property {string} updatePostBack - Rota para atualizar postback por ID
 * @property {string} getSettings - Rota para obter configurações
 * @property {string} updateSetting - Rota para atualizar configurações
 * // ... outras rotas
 */

/**
 * @method getWithCancelByRoute
 * @async
 * @param {string} suffix - Sufixo da URL da requisição
 * @param {Object} params - Parâmetros da requisição
 * @param {string} routeKey - Chave única para identificar a rota
 * @returns {Promise<AxiosResponse>} Resposta da requisição
 * @throws {Error} Erro na requisição ou quando cancelada
 * @description Realiza requisições GET com suporte a cancelamento por rota específica
 */

/**
 * @method get
 * @async
 * @param {string} suffix - Sufixo da URL da requisição
 * @param {Object} [params] - Parâmetros opcionais da requisição
 * @param {AbortSignal} [params.signal] - Sinal para cancelamento da requisição
 * @returns {Promise<AxiosResponse>} Resposta da requisição
 * @throws {Error} Erro na requisição ou quando o token expirou
 * @description Realiza requisições GET básicas com suporte a cancelamento
 */

/**
 * @method login
 * @async
 * @param {string} email - Email do usuário
 * @param {string} password - Senha do usuário
 * @param {string} ipAddress - Endereço IP do usuário
 * @returns {Promise<Object>} Dados do usuário autenticado
 * @throws {Error} Erro durante o processo de login
 * @description Realiza a autenticação do usuário no sistema
 */

/**
 * @method getUsers
 * @async
 * @param {Object} params - Parâmetros de filtro e paginação
 * @param {string} [params.sort='id'] - Campo para ordenação
 * @param {string} [params.dir='desc'] - Direção da ordenação (asc/desc)
 * @param {string} [params.fullName] - Filtro por nome completo
 * @param {string} [params.phoneNumber] - Filtro por telefone
 * @param {string} [params.email] - Filtro por email
 * @param {string} [params.cpf] - Filtro por CPF
 * @param {Object} options - Opções adicionais
 * @param {AbortSignal} options.signal - Sinal para cancelamento da requisição
 * @returns {Promise<Array>} Lista de usuários
 * @throws {Error} Erro ao buscar usuários
 */

// eslint-disable-next-line no-unused-vars
import axios, { AxiosResponse, AxiosError } from "axios";
import { verifyTokenExpired } from "./jwt";
import { LocalStorageService } from "./LocalStorageService";

/**
 * Classe ApiService:
 *
 * Dispõe serviços de acesso a api do bigcupom.
 * @since 1.0.0
 */
class ServiceApi {
  /**
   * Responsável por gerir globalmente as respostas da api.
   * @param {AxiosResponse<any>} response objeto AxiosReponse
   * @returns {AxiosResponse<any>} response
   * retorna o mesmo AxiosResponse recebido como parâmetro
   */
  #handleResponse = (response) => {
    return response;
  };
  /**
   * Responsável por gerir globalmente os erros de acesso a api.
   * @param {AxiosError<any>} error objeto AxiosError
   * @returns {Error} error objeto contendo detalhes
   * do erro
   */
  #handleError = (error) => Promise.reject(error);

  constructor() {
    /**
     * Propriedade routes.
     * Contém os valores das rotas para api.
     * Toda vez em que for necessário criar
     * algum método na classe para chamar a api
     * não esquecer de registrar a rota aqui para
     * utilizá-la.
     */
    this.routes = {
      signin: "/api/admin/user/signin",
      signinCode: "/api/admin/administrator/sign-code",
      resendCode: "/api/admin/administrator/resend-code",
      remainingAttempsCode: "api/admin/administrator/remaining-attempts-code",
      me: "/api/admin/user/get-me",
      listUsers: "/api/admin/user/list-user-points",
      getUsersId: "/api/admin/user/get-user-points/{id}",
      validCpf: "/api/admin/user/validate-cpf/{id}",
      updateUsersId: "api/admin/user/update-user-points/{id}",
      removeUserById: "/api/admin/user/delete-user/{id}",
      updateUserStatusMission: "api/admin/user/update-status-mission/{id}",
      getSmsByUserId: "api/admin/user/get-sms/{id}",
      listReferralsByUserId: "/api/admin/user/list-referrals/{id}",
      getBannerImages: "/api/admin/banner/get-all",
      newBannerImage: "api/admin/banner/new-banner",
      orderCategory: "/api/admin/category/sort",
      bannerActive: "/api/admin/banner/active/{id}",
      getBannerId: "/api/admin/banner/get-banner/{id}",
      updateBanner: "/api/admin/banner/update-banner/{id}",
      getCarouselImages: "/api/admin/carousel/get-carousel-images",
      newCarouselImage: "/api/admin/carousel/new-carousel-item",
      uploadImageCarousel: "/api/admin/carousel/upload-image",
      orderCarousel: "/api/admin/carousel/sort-images",
      carouselActive: "/api/admin/carousel/active/{id}",
      getCarouselId: "/api/admin/carousel/get-one-item/{id}",
      updateCarousel: "/api/admin/carousel/update-one-item/{id}",
      getPrize: "/api/admin/prize/get",
      PrizeById: "/api/admin/prize/get/{id}",
      getStores: "/api/admin/store/get-all",
      getStoresList: "/api/admin/store/list",
      storeDelete: "/api/admin/store/delete-one/{id}",
      uploadImageStore: "/api/admin/store/upload-image",
      getStoreId: "/api/admin/store/find-one/{id}",
      updateStore: "/api/admin/store/update-one/{id}",
      newStore: "/api/admin/store/new-store",
      getStoresId: "/api/admin/store/find-one/{id}",

      getAllApprovalReceipt: "/api/admin/user-store/get-all",
      getImageReceiptCoupon: "/api/admin/user-store/get-image/{id}",
      getReceiptDetailsById: "/api/admin/user-store/get-one/{id}",
      setReceiptById: "/api/admin/user-store/update/{id}",

      getCoupons: "/api/admin/coupon/get-coupons",
      getCouponsId: "/api/admin/coupon/get-coupons/{id}",
      newCoupon: "/api/admin/coupon/new-coupon",
      updateCoupon: "/api/admin/coupon/update-coupons/{id}",
      updateCouponCategoryById: "/api/admin/coupon-category/update/{id}",
      getCouponCategory: "/api/admin/coupon-category/get-all",
      listCouponCategory: "/api/admin/coupon-category/list",
      newCouponCategory: "/api/admin/coupon-category/create",

      getCategory: "/api/admin/category/get",
      getCategoryId: "/api/admin/category/get/{id}",
      newCategory: "/api/admin/category/create",
      updateCategory: "/api/admin/category/update/{id}",
      updatePrize: "/api/admin/prize/update/{id}",
      newPrize: "/api/admin/prize/save",
      storeActive: "/api/admin/store/active/{id}",
      couponActive: "/api/admin/coupon/active/{id}",
      prizeActive: "/api/admin/prize/active/{id}",
      getusersAdmin: "/api/admin/administrator/list-users",
      getusersAdminId: "/api/admin/administrator/get-user/{id}",
      updateuserAdminId: "/api/admin/administrator/update/{id}",
      newusersAdmin: "/api/admin/administrator/create",
      setPassword: "/api/admin/user/change-password",
      setadminUser: "/api/admin/user/update-me",
      setadminUserActive: "/api/admin/administrator/set-active/{id}",
      getallGroups: "/api/admin/group/get-all",
      getGroupId: "/api/admin/group/get/{id}",
      groupActive: "/api/admin/group/active/{id}",
      newGroup: "/api/admin/group/create",
      updateGroupById: "/api/admin/group/update/{id}",
      getallModules: "/api/admin/module/get-all",
      newModule: "/api/admin/module/create",
      updateModule: "/api/admin/module/update/{id}",
      activeModule: "/api/admin/module/active/{id}",
      getModuleId: "/api/admin/module/get-one/{id}",
      orderModules: "/api/admin/module/sort",
      getMenus: "/api/admin/category/sidebar",
      getGroupActives: "/api/admin/group/get-active",
      generateCoupon: "/api/v1/admin/coupon/generate-coupons",
      statusGenerateCoupons: "/api/v1/admin/coupon/all-generate-coupons",
      generateAllCoupon: "/api/v1/admin/coupon/get-pre-coupons",
      sendPreCoupon: "/api/v1/admin/coupon/generate-coupon/{id}",
      getPreCouponId: "/api/v1/admin/coupon/show-precoupon/{id}",
      uploadImageCoupon: "/api/admin/coupon/upload-image",
      deletePreCouponId: "/api/v1/admin/coupon/delete-pre-coupon/{id}",
      sendPreCouponRekuten: "/api/admin/coupon/add-coupom-rakuten",
      syncPreCouponRekuten: "/api/admin/coupon/add-pre-coupons-rakuten",
      getPreCouponRekuten: "/api/admin/coupon/get-all-pre-coupons-rakuten",
      activePreCouponRekuten:
        "/api/admin/coupon/active-pre-coupon-rakuten/{id}",
      getPreCouponByIdRekuten:
        "/api/admin/coupon/get-id-pre-coupom-rakuten/{id}",
      information: "/api/admin/dashboard/get-data",
      getAffiliates: "/api/admin/affiliate/get-all",
      newAffiliate: "/api/admin/affiliate/new-affiliate",
      updateAffiliate: "/api/admin/affiliate/update/{id}",
      affiliateActive: "/api/admin/affiliate/active/{id}",
      getRegisters: "/api/admin/reports/registers",
      getReportsLogin: "/api/admin/reports/login",
      uploadImageRewards: "/api/admin/reward/upload-image",
      uploadImageRewardsIcon: "/api/admin/reward/upload-image-icon",
      uploadImageCoverRewards: "/api/admin/reward/upload-image-cover",
      uploadImageProduct: "/api/admin/reward/upload-image-product",
      newRewards: "/api/admin/reward/create",
      getRewards: "/api/admin/reward/get-all",
      rewardActive: "/api/admin/reward/active/{id}",
      getRewardId: "/api/admin/reward/get-one/{id}",
      getRewardsMinu: "/api/admin/reward/get-all-rewards-api",
      generateGiftCard: "/api/admin/reward/send-voucher-file",
      getGiftCard: "/api/admin/reward/get-vouchers",
      getGenerateGiftCardById: "/api/admin/reward/get-voucher/{id}",
      getStatusGenerateGiftCard: "/api/admin/reward/get-files-voucher",
      updateRewardId: "/api/admin/reward/update/{id}",
      newRewardCategory: "/api/admin/reward/category/create",
      getRewardCategories: "/api/admin/reward/category/get-all",
      getRewardCategoryById: "/api/admin/reward/category/get-one/{id}",
      updateRewardCategoryById: "/api/admin/reward/category/update/{id}",
      rewardCategoryList: "/api/admin/reward/category/list",
      getReportsReward: "/api/admin/reports/get-rewards",
      getReportsRewardData: "/api/admin/reports/rewards",
      getReportsAffiliatesLogin: "/api/admin/reports/affiliates-login",
      getReportsQueueAffiliatesLogin: "/api/admin/reports/queue-report",
      getReportsRanking: "/api/admin/reports/ranking",
      getReportsMission: "/api/admin/reports/mission",
      getReportsQueueMission: "/api/admin/reports/queue-report",
      updatemissionId: "/api/admin/mission/update/{id}",
      newMission: "/api/admin/mission/create",
      getMissions: "/api/admin/mission/get-all",
      getMissionsDependecy: "/api/admin/mission/list-all",
      getOrderMissions: "/api/admin/mission/order-list",
      uploadImageMissions: "/api/admin/mission/upload-image",
      missionActive: "/api/admin/mission/active/{id}",
      orderMission: "/api/admin/mission/sort",
      replacement: "/api/admin/exchange/get-all",
      updateReplacementStatus: "/api/admin/exchange/update/{id}",
      getReplacementDetailsById: "/api/admin/exchange/get-details/{id}",
      getNotifications: "/api/admin/notifications/get-all",
      updateNotificationStatusById: "/api/admin/notifications/update/{id}",
      updateAllNotificationStatus: "/api/admin/notifications/update-all",
      listHotOffers: "api/admin/offers/list",
      createHotOffers: "api/admin/offers",
      updateHotOffers: "api/admin/offers/{id}",
      getPostback: "api/admin/postback/list",
      createPostback: "api/admin/postback/create",
      updatePostBack: "api/admin/postback/update/{id}",
      getSettings: "/api/admin/setting/get-settings",
      updateSetting: "api/admin/setting/generate",
      getLandingPage: "/api/admin/landingpage/get-all",
      newLandingPage: "/api/admin/landingpage/create",
      uploadImageLandingPage: "/api/admin/landingpage/upload-image",
      updateLandingPage: "/api/admin/landingpage/update/{id}",
      deleteLandingPage: "/api/admin/landingpage/delete/{id}",
      getLandingPageById: "/api/admin/landingpage/get-id/{id}",
      /* categoria review */
      newReviewCategory: "/api/admin/product/category/create",
      setReviewCategory: "/api/admin/product/category/update/{id}",
      deleteReviewCategory: "/api/admin/product/category/delete/{id}",
      deleteQuestionReviewCategory: "/api/admin/product/question/delete/{id}",
      deleteQuestionReviewCategoryId:
        "/api/admin/product/question/delete-questions",
      getReviewCategoryById: "/api/admin/product/category/get-one/{id}",
      getAllReviewCategory: "/api/admin/product/category/get-all",
      getAllReviewCategoryList: "/api/admin/product/category/list",
      getQuestionsByCategory:
        "/api/admin/product/question/get-questions-by-category/{id}",
      newQuestionsByCategory: "/api/admin/product/question/create-questions",
      setQuestionsByCategory: "/api/admin/product/question/update-questions",
      setQuestionByQuestionId: "/api/admin/product/question/update/{id}",
      uploadImageCategory: "/api/admin/product/upload-image",

      /* Product review */
      findProductsMLList: "/api/admin/product/find-products",
      getProductsList: "/api/admin/product/get-all",
      newProductReview: "/api/admin/product/create",
      setProductReview: "/api/admin/product/update/{id}",
      getProductById: "/api/admin/product/get-one/{id}",
      uploadProductImage: "api/admin/product/upload-image",
      findAllProducts: "/api/admin/product/find-products",
      listBrand: "/api/admin/product/brand/list",

      /* Review review */
      getReviewList: "/api/admin/product/review/get-all",
      getReviewById: "/api/admin/product/review/get-one/{id}",
      setReviewById: "/api/admin/product/review/update/{id}",

      /* Review brand */
      getAllBrandReview: "/api/admin/product/brand/get-all",
      newBrandReview: " /api/admin/product/brand/create",
      setBrandReview: "/api/admin/product/brand/update/{id}",
      getBrandById: "/api/admin/product/brand/get-one/{id}",

      /* Aprovação de missão */
      getAllApprovalMission: "/api/admin/mission-approval/get-all",
      setApprovalMissionById: "/api/admin/mission-approval/approval/{id}",
      setApprovalMissionStepById:
        "/api/admin/mission-approval/approval/{id}/step/{stepId}",

      /* Categoria de missão  */
      listMissionCategory: "/api/admin/mission/category/get-all",
      newMissionCategory: "/api/admin/mission/category/create",
      updateMissionCategory: "/api/admin/mission/category/update/{id}",
      getMissionCategoryById: "/api/admin/mission/category/get-one/{id}",

      /* Subcategoria de missão  */
      listMissionSubcategory: "/api/admin/mission/subcategory/get-all",
      newMissionSubcategory: "/api/admin/mission/subcategory/create",
      updateMissionSubcategory: "/api/admin/mission/subcategory/update/{id}",
      getMissionSubcategoryById: "/api/admin/mission/subcategory/get-one/{id}",

      /* Roleta  */
      getAllRouletteItems: "/api/admin/roulette/get",
      newRouletteItem: "/api/admin/roulette/create",
      setRouletteItemById: "/api/admin/roulette/update/{id}",
      deleteRoulleteItem: "/api/admin/roulette/delete/{id}",
      /* Relatorio da roleta */
      reportsRoulette: "/api/admin/roulette/report/get-list",
      /* Configuração roleta */
      getRoulettePackageSettings: "/api/admin/roulette/packages/get-list",
      setRoulettePackageSettings: "/api/admin/roulette/packages/update",
      /* Cofiguração usuario premium */
      getSettingsUserPremium: "/api/admin/setting/premium/get-list",
      setSettingsUserPremium: "/api/admin/setting/premium/update",
      getPremiumRewards: "/api/admin/setting/premium/get-rewards",
      setPremiumRewards: "/api/admin/setting/premium/update-rewards",

      /* Configuração para tela de códigos */
      getAllCodeList: "/api/admin/redeem-code/get-list",
      newRedeemCode: "/api/admin/redeem-code/create",
      setRedeemCode: "/api/admin/redeem-code/update/{id}",
      activeRedeemCode: "/api/admin/redeem-code/active/{id}",

      /* Loja Turbinada */
      getBoostedStores: "/api/admin/store/get-all-store-schedule-turbo",
      createBoostedStore: "/api/admin/store/new-store-schedule-turbo",
      updateBoostedStore: "/api/admin/store/update-store-schedule-turbo/{id}",

      /* Quiz / Pesquisa (Mapped to Survey in backend) */
      getQuizzes: "api/admin/survey/get-all",
      newQuiz: "api/admin/survey/create",
      updateQuiz: "api/admin/survey/update/{id}",
      getQuizById: "api/admin/survey/get-one/{id}",
      deleteQuiz: "api/admin/survey/delete-one/{id}",
      uploadSurveyImage: "api/admin/survey/upload-image",
      addQuizQuestion: "api/admin/survey/question/create",
      addQuizQuestionWithOptions:
        "api/admin/survey/question/create-with-options",
      updateQuizQuestionWithOptions:
        "api/admin/survey/question/update-with-options/{surveyId}",
      updateQuizQuestion: "api/admin/survey/question/update/{id}",
      deleteQuizQuestion: "api/admin/survey/question/delete/{id}",
      orderSurvey: "api/admin/survey/order/{id}",
      getSurveyList: "api/admin/survey/list",
      getSurveyStatistics: "api/admin/survey/statistics/{id}",
      requestSurveyExport: "api/admin/survey/request-export",

      /* Relatório de Missões Especiais (Pesquisa) */
      getSpecialMissionReport: "/api/admin/reports/special-mission/get-report",
      exportSpecialMissionReport:
        "/api/admin/reports/special-mission/export-report",
      getSpecialMissionStatistics:
        "/api/admin/reports/special-mission/get-statistics",
      getAllSpecialMissions:
        "/api/admin/reports/special-mission/get-all-research-mission",

      /* Relatório de Indicações */
      getReportsReferral: "/api/admin/reports/referral",
      getReportsReferralDetail: "/api/admin/reports/referral-detail/{id}",

      /* Relatório de Status de Missão */
      exportMissionStatusReport:
        "/api/admin/reports/mission-status/export-report",

      /* Partidas de Futebol */
      getAllMatches: "/api/admin/matrix-match/get-all-matches",

      /* Big Torcida - Aprovação de Cartelas */
      getWinnersPending: "/api/admin/matrix-match/get-winners-pending",
      getGameCardById: "/api/admin/matrix-match/get-game-card/{id}",
      approvalGameCard: "/api/admin/matrix-match/approval-game-card/{id}",
      exportBaseGameCard: "/api/admin/matrix-match/export-base",

      /* Processamento Colgate - CSV */
      colgateCsvUpload: "/api/admin/colgate/upload-csv",
      colgateCsvHistory: "/api/admin/colgate/history",
      colgateCsvDownloadErrors: "/api/admin/colgate/download-errors/{jobId}",
      colgateCsvCancel: "/api/admin/colgate/cancel/{jobId}",
    };

    this.baseURL = process.env.REACT_APP_API_URL;
    this.defaultAxios = axios.create({
      baseURL: this.baseURL,
    });
    this.token = "";
    this.defaultAxios.interceptors.response.use(
      this.#handleResponse,
      this.#handleError,
    );

    // Adiciona um objeto para gerenciar tokens de cancelamento por rota
    this.cancelTokenSources = {};
  }

  /**
   * Redefine o cabeçalho de cada requisição
   * adicionando o token de autorização
   */
  #setAuthorizationHeader = () => {
    //this.token = JSON.parse(localStorage.getItem('token'));
    this.token = LocalStorageService.getItem("accessToken");
    // set token
    this.defaultAxios.defaults.headers.common["Authorization"] =
      `Bearer ${this.token}`;
  };

  /**
   * Realiza requisições do tipo get com cancelamento específico por rota
   */
  async getWithCancelByRoute(suffix, params, routeKey) {
    this.#setAuthorizationHeader();
    try {
      // Cancela requisição anterior da mesma rota se existir
      if (this.cancelTokenSources[routeKey]) {
        this.cancelTokenSources[routeKey].cancel(
          "Cancelando requisição anterior de " + routeKey,
        );
      }

      // Cria novo token de cancelamento para esta rota
      this.cancelTokenSources[routeKey] = axios.CancelToken.source();

      const response = await this.defaultAxios.get(suffix, {
        params,
        cancelToken: this.cancelTokenSources[routeKey].token,
      });

      return response;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.debug("Requisição cancelada:", err.message);
      } else if (verifyTokenExpired()) {
        localStorage.clear();
        window.location.reload();
      }
      throw err;
    }
  }

  /**
   * Realiza requisições do tipo get
   * @param {string} suffix string url
   * @param {object} parameters object parametros
   * @returns {Promise<any>} defaultAxios
   */
  async get(suffix, params) {
    this.#setAuthorizationHeader();
    try {
      // Extrai o signal dos params se existir
      const { signal, ...restParams } = params || {};

      const response = await this.defaultAxios.get(suffix, {
        params: restParams,
        signal, // Passa o signal para o axios
      });
      return response;
    } catch (err) {
      if (axios.isCancel(err)) {
        // Requisição foi cancelada
        console.debug("Request canceled:", err.message);
        return;
      }

      if (verifyTokenExpired()) {
        localStorage.clear();
        window.location.reload();
      }
      throw err;
    }
  }

  /**
   * Realiza requisições do tipo get com porcentage
   * @param {string} suffix string url
   * @param {object} parameters object parametros
   * @returns {Promise<any>} defaultAxios
   */
  async getPorcent(suffix, config) {
    this.#setAuthorizationHeader();
    return await this.defaultAxios.get(suffix, config);
  }

  /**
   * Realiza requisições do tipo post
   * @param {string} suffix string url
   * @param {object} parameters object parametros
   * @returns {Promise<any>} defaultAxios
   */
  async post(suffix, parameters) {
    this.#setAuthorizationHeader();
    const iosBugFixHeader = {
      Headers: {
        "Content-Type": "text/plain",
      },
    };
    const response = await this.defaultAxios.post(
      suffix,
      parameters,
      iosBugFixHeader,
    );
    return response;
  }

  /**
   * Realiza requisições do tipo patch
   * @param {string} suffix string url
   * @param {object} parameters object parametros
   * @returns {Promise<any>} defaultAxios
   */
  async patch(suffix, parameters) {
    this.#setAuthorizationHeader();
    const response = await this.defaultAxios.patch(suffix, parameters);
    return response;
  }

  /**
   * Realiza requisições do tipo patch
   * @param {string} suffix string url
   * @param {object} parameters object parametros
   * @returns {Promise<any>} defaultAxios
   */
  async delete(suffix, parameters) {
    this.#setAuthorizationHeader();
    const response = await this.defaultAxios.delete(suffix, parameters);
    return response;
  }

  /**
   * Realiza o login na api e retorna dados do usuário.
   * @param {string} email - email do usuário
   * @param {string} password  - senha do usuário
   */
  async login(email, password, ipAddress) {
    try {
      const params = { email, password, ipAddress };
      const { signin } = this.routes;
      const { data } = await this.post(signin, params);
      return data;
    } catch (error) {
      return error;
    }
  }
  async loginStepTwo(email, code, ipAddress) {
    try {
      const params = { email, code, ipAddress };
      const { signinCode } = this.routes;
      const { data } = await this.post(signinCode, params);
      return data;
    } catch (error) {
      return error;
    }
  }
  async loginResendCode(email, ipAddress) {
    try {
      const params = { email, ipAddress };
      const { resendCode } = this.routes;
      const { data } = await this.post(resendCode, params);
      return data;
    } catch (error) {
      return error;
    }
  }
  async remainingAttempsCode(email) {
    try {
      const params = { email };
      const { remainingAttempsCode } = this.routes;
      const { data } = await this.post(remainingAttempsCode, params);
      return data;
    } catch (error) {
      return error;
    }
  }

  /**
   * Método responsável por tentar obter dados do usuário
   * logado:
   *
   * Obs: use-o somente se o token de acesso do usuário
   *
   * "accessToken" existir no armazenamento local.
   */
  async me() {
    try {
      const { me } = this.routes;
      const { data } = await this.get(me);
      return data;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Obtém lista de usuários do bigcupom.
   * @returns {Array<object>|Error}
   */
  async getUsers(params, { signal }) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }
      if (params.fullName === "") {
        params.fullName = undefined;
      }
      if (params.phoneNumber === "") {
        params.phoneNumber = undefined;
      }
      if (params.email === "") {
        params.email = undefined;
      }
      if (params.cpf === "") {
        params.cpf = undefined;
      }

      const { listUsers } = this.routes;
      const data = await this.getWithCancelByRoute(listUsers, params, "users");
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Busca um usuário por id
   * @param {number} id - id do usuário
   * @returns {Promise<any>|Error}
   */

  async getUserById(id) {
    try {
      const getUsersId = this.routes.getUsersId.replace("{id}", id);
      const data = await this.get(getUsersId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async upadateUserById(id, params) {
    try {
      const updateUsersId = this.routes.updateUsersId.replace("{id}", id);
      const data = await this.patch(updateUsersId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async removeUserById(id, params) {
    try {
      const removeUserById = this.routes.removeUserById.replace("{id}", id);
      const data = await this.delete(removeUserById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async upadateCpfUserById(id, params) {
    try {
      const validCpf = this.routes.validCpf.replace("{id}", id);
      const data = await this.patch(validCpf, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateUserStatusMission(id, params) {
    try {
      const updateUserStatusMission =
        this.routes.updateUserStatusMission.replace("{id}", id);
      const data = await this.patch(updateUserStatusMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getSmsByUserId(id) {
    try {
      const getSmsByUserId = this.routes.getSmsByUserId.replace("{id}", id);
      const data = await this.get(getSmsByUserId);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista de convites de usuário por ID
   * @param {number} id - id do usuário
   * @param {object} params - parâmetros de consulta
   * @param {object} options - opções para cancelamento
   * @returns {Promise<any>|Error}
   */
  async listReferralsByUserId(id, params = {}, { signal } = {}) {
    try {
      const listReferralsByUserId = this.routes.listReferralsByUserId.replace(
        "{id}",
        id,
      );
      const data = await this.getWithCancelByRoute(
        listReferralsByUserId,
        params,
        `referrals-${id}`,
        { signal },
      );
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getStoreById(id) {
    try {
      const getStoreId = this.routes.getStoreId.replace("{id}", id);
      const data = await this.get(getStoreId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getRewardById(id) {
    try {
      const getRewardId = this.routes.getRewardId.replace("{id}", id);
      const data = await this.get(getRewardId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getPreCouponByIdRekuten(id) {
    try {
      const getPreCouponByIdRekuten =
        this.routes.getPreCouponByIdRekuten.replace("{id}", id);
      const data = await this.get(getPreCouponByIdRekuten);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getCouponById(id) {
    try {
      const getCouponsId = this.routes.getCouponsId.replace("{id}", id);
      const data = await this.get(getCouponsId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getPreCouponById(id) {
    try {
      const getPreCouponId = this.routes.getPreCouponId.replace("{id}", id);
      const data = await this.get(getPreCouponId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async sendPreCoupon(id) {
    try {
      const sendPreCoupon = this.routes.sendPreCoupon.replace("{id}", id);
      const data = await this.get(sendPreCoupon);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getModuleById(id) {
    try {
      const getModuleId = this.routes.getModuleId.replace("{id}", id);
      const data = await this.get(getModuleId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getCategoryById(id) {
    try {
      const getCategoryId = this.routes.getCategoryId.replace("{id}", id);
      const data = await this.get(getCategoryId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setStoreActive(id) {
    try {
      const storeActive = this.routes.storeActive.replace("{id}", id);
      const data = await this.patch(storeActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setMissionActive(id) {
    try {
      const missionActive = this.routes.missionActive.replace("{id}", id);
      const data = await this.patch(missionActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async activePreCouponRekuten(id) {
    try {
      const activePreCouponRekuten = this.routes.activePreCouponRekuten.replace(
        "{id}",
        id,
      );
      const data = await this.get(activePreCouponRekuten);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setGroupActive(id) {
    try {
      const groupActive = this.routes.groupActive.replace("{id}", id);
      const data = await this.post(groupActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setModuleActive(id) {
    try {
      const activeModule = this.routes.activeModule.replace("{id}", id);
      const data = await this.patch(activeModule);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setAdminUserActive(id) {
    try {
      const setadminUserActive = this.routes.setadminUserActive.replace(
        "{id}",
        id,
      );
      const data = await this.patch(setadminUserActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setCouponActive(id) {
    try {
      const couponActive = this.routes.couponActive.replace("{id}", id);
      const data = await this.post(couponActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setBannerActive(id) {
    try {
      const bannerActive = this.routes.bannerActive.replace("{id}", id);
      const data = await this.patch(bannerActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getCarouselById(id) {
    try {
      const getCarouselId = this.routes.getCarouselId.replace("{id}", id);
      const data = await this.get(getCarouselId);
      return data;
    } catch (error) {
      return error;
    }
  }

  /**
   * Obtém lista de Slides do bigcupom.
   * @returns {Array<object> | Error}
   */
  async getCarousel() {
    try {
      const { getCarouselImages } = this.routes;
      const data = await this.get(getCarouselImages);
      return data;
    } catch (error) {
      return error;
    }
  }
  async setCarouselActive(id) {
    try {
      const carouselActive = this.routes.carouselActive.replace("{id}", id);
      const data = await this.patch(carouselActive);
      return data;
    } catch (error) {
      return error;
    }
  }

  async updateCarouselById(id, params) {
    try {
      const updateCarousel = this.routes.updateCarousel.replace("{id}", id);
      const data = await this.patch(updateCarousel, params);
      return data;
    } catch (error) {
      return error;
    }
  }
  async newCarousel(params) {
    try {
      const { newCarouselImage } = this.routes;
      const data = await this.post(newCarouselImage, params);
      return data;
    } catch (error) {
      return error;
    }
  }

  async orderCarousel(params) {
    try {
      const { orderCarousel } = this.routes;
      const data = await this.post(orderCarousel, params);
      return data;
    } catch (error) {
      return error;
    }
  }

  async setPasswordAdmin(params) {
    try {
      const setPassword = this.routes.setPassword;
      const data = await this.patch(setPassword, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setAccountAdmin(params) {
    try {
      const setadminUser = this.routes.setadminUser;
      const data = await this.patch(setadminUser, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setPrizeActived(id) {
    try {
      const prizeActive = this.routes.prizeActive.replace("{id}", id);
      const data = await this.patch(prizeActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setRewardActived(id) {
    try {
      const rewardActive = this.routes.rewardActive.replace("{id}", id);
      const data = await this.patch(rewardActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateStoreById(id, params) {
    try {
      const updateStore = this.routes.updateStore.replace("{id}", id);
      const data = await this.patch(updateStore, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateLandingPageById(id, params) {
    try {
      const updateLandingPage = this.routes.updateLandingPage.replace(
        "{id}",
        id,
      );
      const data = await this.patch(updateLandingPage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateRewardsById(id, params) {
    try {
      const updateRewardId = this.routes.updateRewardId.replace("{id}", id);
      const data = await this.patch(updateRewardId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateMissionsById(id, params) {
    try {
      const updatemissionId = this.routes.updatemissionId.replace("{id}", id);
      const data = await this.patch(updatemissionId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async orderMission(params) {
    try {
      const { orderMission } = this.routes;
      const data = await this.patch(orderMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateModuleById(id, params) {
    try {
      const updateModule = this.routes.updateModule.replace("{id}", id);
      const data = await this.patch(updateModule, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateuserAdminById(id, params) {
    try {
      const updateuserAdminId = this.routes.updateuserAdminId.replace(
        "{id}",
        id,
      );
      const data = await this.patch(updateuserAdminId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updatePrizeById(id, params) {
    try {
      const updatePrize = this.routes.updatePrize.replace("{id}", id);
      const data = await this.patch(updatePrize, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateReplacementStatus(id, params) {
    try {
      const updateReplacementStatus =
        this.routes.updateReplacementStatus.replace("{id}", id);
      const data = await this.post(updateReplacementStatus, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateNotificationStatusById(id) {
    try {
      const updateNotificationStatusById =
        this.routes.updateNotificationStatusById.replace("{id}", id);
      const data = await this.get(updateNotificationStatusById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateAllNotificationStatus() {
    try {
      const updateAllNotificationStatus =
        this.routes.updateAllNotificationStatus;
      const data = await this.get(updateAllNotificationStatus);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateCouponById(id, params) {
    try {
      const updateCoupon = this.routes.updateCoupon.replace("{id}", id);
      const data = await this.patch(updateCoupon, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateCategoryById(id, params) {
    try {
      const updateCategory = this.routes.updateCategory.replace("{id}", id);
      const data = await this.patch(updateCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getBannerById(id) {
    try {
      const getBannerId = this.routes.getBannerId.replace("{id}", id);
      const data = await this.get(getBannerId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getGroupById(id) {
    try {
      const getGroupId = this.routes.getGroupId.replace("{id}", id);
      const data = await this.get(getGroupId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getUsersAdminId(id) {
    try {
      const getusersAdminId = this.routes.getusersAdminId.replace("{id}", id);
      const data = await this.get(getusersAdminId);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista de Slides do bigcupom.
   * @returns {Array<object> | Error}
   */
  async getBanner() {
    try {
      const { getBannerImages } = this.routes;
      const data = await this.get(getBannerImages);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getDashboard() {
    try {
      const { information } = this.routes;
      const data = await this.get(information);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getMenus(signal) {
    try {
      const { getMenus } = this.routes;
      const data = await this.getWithCancelByRoute(getMenus, {}, "menus");
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getGroups() {
    try {
      const { getallGroups } = this.routes;
      const data = await this.get(getallGroups);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getGroupActives() {
    try {
      const { getGroupActives } = this.routes;
      const data = await this.get(getGroupActives);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getUsersAdmin() {
    try {
      const { getusersAdmin } = this.routes;
      const data = await this.get(getusersAdmin);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  /**
   * Obtém lista de Numeros da Sorte do bigcupom.
   * @returns {Array<object> | Error}
   */
  async getPrize() {
    try {
      const { getPrize } = this.routes;
      const data = await this.get(getPrize);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getRewards(params, { signal } = {}) {
    try {
      // Map React Query params to backend expected params
      const backendParams = {
        limit: String(params.limit || 25),
        skip: String(params.skip || 0),
        sort: params.sort || "id",
        dir: params.dir || "desc",
      };

      // Add filters if they exist
      if (params.title) {
        backendParams.title = params.title;
      }

      const { getRewards } = this.routes;
      const data = await this.get(getRewards, { ...backendParams, signal });
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getRewardsMinu() {
    try {
      const { getRewardsMinu } = this.routes;
      const data = await this.get(getRewardsMinu);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async createRewardCategory(params) {
    try {
      const { newRewardCategory } = this.routes;
      const data = await this.post(newRewardCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getRewardCategories(params, { signal } = {}) {
    try {
      // Map React Query params to backend expected params
      const backendParams = {
        limit: String(params.limit || 25),
        skip: String(params.skip || 0),
        sort: params.sort || "order",
        dir: params.dir || "asc",
      };

      // Add filters if they exist
      if (params.name) {
        backendParams.name = params.name;
      }

      const { getRewardCategories } = this.routes;
      const data = await this.get(getRewardCategories, {
        ...backendParams,
        signal,
      });
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getRewardCategoryById(id) {
    try {
      const getRewardCategoryById = this.routes.getRewardCategoryById.replace(
        "{id}",
        id,
      );
      const data = await this.get(getRewardCategoryById);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateRewardCategoryById(id, params) {
    try {
      const updateRewardCategoryById =
        this.routes.updateRewardCategoryById.replace("{id}", id);
      const data = await this.patch(updateRewardCategoryById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getRewardCategoryList() {
    try {
      const { rewardCategoryList } = this.routes;
      const data = await this.get(rewardCategoryList);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getReplacements(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "statusExchangeId";
        params.dir = "asc";
      }
      if (params.sort === undefined) {
        params.sort = "statusExchangeId";
      }
      if (params.dir === undefined) {
        params.dir = "asc";
      }
      const { replacement } = this.routes;
      const data = await this.get(replacement, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReplacementDetailsById(id) {
    try {
      const getReplacementDetailsById =
        this.routes.getReplacementDetailsById.replace("{id}", id);
      const data = await this.get(getReplacementDetailsById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getPrizeById(id) {
    try {
      const PrizeById = this.routes.PrizeById.replace("{id}", id);
      const data = await this.get(PrizeById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getLandingPageById(id) {
    try {
      const getLandingPageById = this.routes.getLandingPageById.replace(
        "{id}",
        id,
      );
      const data = await this.get(getLandingPageById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async deletePreCouponId(id) {
    try {
      const deletePreCouponId = this.routes.deletePreCouponId.replace(
        "{id}",
        id,
      );
      const data = await this.delete(deletePreCouponId);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  /**
   * Obtém lista de lojas do bigcupom.
   * @returns {Array<object> | Error}
   */
  async getStore() {
    try {
      const { getStores } = this.routes;
      const data = await this.get(getStores);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista otimizada de lojas (apenas id e nome) para filtros e autocomplete.
   * @returns {Array<object> | Error}
   */
  async getStoresList() {
    try {
      const { getStoresList } = this.routes;
      const data = await this.get(getStoresList);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getOrderMissions() {
    try {
      const { getOrderMissions } = this.routes;
      const data = await this.get(getOrderMissions);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getMissionsDependecy() {
    try {
      const { getMissionsDependecy } = this.routes;
      const data = await this.get(getMissionsDependecy);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getMission(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "createdAt";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "createdAt";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }

      const { getMissions } = this.routes;
      const data = await this.get(getMissions, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async storeDelete(id) {
    try {
      const storeDelete = this.routes.storeDelete.replace("{id}", id);
      const data = await this.delete(storeDelete);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async landingPageDelete(id) {
    try {
      const deleteLandingPage = this.routes.deleteLandingPage.replace(
        "{id}",
        id,
      );
      const data = await this.delete(deleteLandingPage);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  /**
   * Obtém lista de cupons do bigcupom.
   * @returns {Array<object> | Error}
   */
  async getCategory() {
    try {
      const { getCategory } = this.routes;
      const data = await this.get(getCategory);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getCoupons(params = {}) {
    try {
      // Simplificar e otimizar a validação de parâmetros
      const cleanParams = {
        q: params.q || "",
        sort: params.sort || "createdAt",
        dir: params.dir || "desc",
        limit: params.limit || 25,
        skip: params.skip || 0,
      };

      // Só adicionar parâmetros opcionais se tiverem valor válido
      if (params.storeId && params.storeId !== "all") {
        cleanParams.storeId = params.storeId;
      }
      if (params.couponCategoryId && params.couponCategoryId !== "all") {
        cleanParams.couponCategoryId = params.couponCategoryId;
      }
      if (params.couponType && params.couponType !== "all") {
        cleanParams.couponType = params.couponType;
      }
      if (params.active && params.active !== "all") {
        cleanParams.active = params.active;
      }
      if (params.featured) {
        cleanParams.featured = params.featured;
      }

      const { getCoupons } = this.routes;
      const data = await this.get(getCoupons, cleanParams);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getCouponCategory() {
    try {
      const { getCouponCategory } = this.routes;
      const data = await this.get(getCouponCategory);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async listCouponCategory() {
    try {
      const { listCouponCategory } = this.routes;
      const data = await this.get(listCouponCategory);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateCouponCategoryById(id, params) {
    try {
      const updateCouponCategoryById =
        this.routes.updateCouponCategoryById.replace("{id}", id);
      const data = await this.patch(updateCouponCategoryById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newCouponCategory(params) {
    try {
      const { newCouponCategory } = this.routes;
      const data = await this.post(newCouponCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getRakutenCoupons() {
    try {
      const { getPreCouponRekuten } = this.routes;
      const data = await this.get(getPreCouponRekuten);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async syncRakutenCoupons(config) {
    try {
      const { syncPreCouponRekuten } = this.routes;
      return await this.getPorcent(syncPreCouponRekuten, config);
    } catch (error) {
      return error.response;
    }
  }
  async getRegisters(params) {
    try {
      const { getRegisters } = this.routes;
      const data = await this.get(getRegisters, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsLogin(params) {
    try {
      const { getReportsLogin } = this.routes;
      const data = await this.get(getReportsLogin, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsReward(params) {
    try {
      const { getReportsReward } = this.routes;
      const data = await this.get(getReportsReward, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsRewardData(params) {
    try {
      const { getReportsRewardData } = this.routes;
      const data = await this.get(getReportsRewardData, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsAffiliatesLogin(params) {
    try {
      const { getReportsAffiliatesLogin } = this.routes;
      const data = await this.get(getReportsAffiliatesLogin, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsQueueAffiliatesLogin(params) {
    try {
      const { getReportsQueueAffiliatesLogin } = this.routes;
      const data = await this.get(getReportsQueueAffiliatesLogin, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReportsRanking(params) {
    try {
      if (!params.startDate || !params.endDate) {
        throw new Error("startDate e endDate são obrigatórios");
      }

      const { getReportsRanking } = this.routes;
      const data = await this.get(getReportsRanking, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getReportsMission(params) {
    try {
      // Se allPeriod for true e missionId estiver presente, não precisa de datas
      const needsDates = !params.allPeriod;
      if (needsDates && (!params.startDate || !params.endDate)) {
        throw new Error(
          "startDate e endDate são obrigatórios quando não usar todo o período",
        );
      }

      // TODO: Remover mock quando backend estiver pronto
      const useMock = true;
      if (useMock) {
        // Importa o mock de arquivo separado
        const { getMockReportsMission } =
          await import("../mocks/reportsMissionMock");
        return getMockReportsMission(params);
      }

      const { getReportsMission } = this.routes;
      const data = await this.get(getReportsMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async requestSurveyExport(params) {
    try {
      const { requestSurveyExport } = this.routes;
      const response = await this.post(requestSurveyExport, params);
      return response;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista de pesquisas (surveys) para autocomplete.
   * @returns {Promise<Object>} Lista de surveys com id e title
   */
  async getSurveyList() {
    try {
      const { getSurveyList } = this.routes;
      const data = await this.get(getSurveyList);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém estatísticas de uma pesquisa (survey) com filtros demográficos.
   * @param {number|string} surveyId - ID da pesquisa
   * @param {Object} [params] - Filtros demográficos opcionais
   * @param {string} [params.gender] - Gênero (M, F, Outro)
   * @param {string} [params.state] - Estado (UF)
   * @param {number} [params.minAge] - Idade mínima
   * @param {number} [params.maxAge] - Idade máxima
   * @returns {Promise<Object>} Estatísticas da pesquisa
   */
  async getSurveyStatistics(surveyId, params = {}) {
    try {
      if (!surveyId) {
        throw new Error("surveyId é obrigatório");
      }
      const route = this.routes.getSurveyStatistics.replace("{id}", surveyId);
      const data = await this.get(route, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getReportsQueueMission(params) {
    try {
      const { getReportsQueueMission } = this.routes;
      const data = await this.get(getReportsQueueMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém relatório paginado de missões especiais (pesquisa)
   * @param {Object} params - Parâmetros da requisição
   * @param {number} params.missionId - ID da missão (obrigatório)
   * @param {number} [params.startDate] - Data inicial (timestamp)
   * @param {number} [params.endDate] - Data final (timestamp)
   * @param {number} [params.skip=0] - Registros a pular
   * @param {number} [params.limit=50] - Registros por página
   * @returns {Promise<Object>} Dados do relatório
   */
  async getSpecialMissionReport(params) {
    try {
      if (!params.missionId) {
        throw new Error("missionId é obrigatório");
      }
      const { getSpecialMissionReport } = this.routes;
      const data = await this.get(getSpecialMissionReport, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Exporta relatório de missões especiais para CSV
   * @param {Object} params - Parâmetros da requisição
   * @param {number} params.missionId - ID da missão (obrigatório)
   * @param {number} [params.startDate] - Data inicial (timestamp)
   * @param {number} [params.endDate] - Data final (timestamp)
   * @returns {Promise<Object>} URL do arquivo CSV no S3
   */
  async exportSpecialMissionReport(params) {
    try {
      if (!params.missionId) {
        throw new Error("missionId é obrigatório");
      }
      const { exportSpecialMissionReport } = this.routes;
      const data = await this.post(exportSpecialMissionReport, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém estatísticas agregadas de missões especiais (para dashboard)
   * @param {Object} params - Parâmetros da requisição
   * @param {number} params.missionId - ID da missão (obrigatório)
   * @param {number} [params.startDate] - Data inicial (timestamp)
   * @param {number} [params.endDate] - Data final (timestamp)
   * @returns {Promise<Object>} Estatísticas agregadas
   */
  async getSpecialMissionStatistics(params) {
    try {
      if (!params.missionId) {
        throw new Error("missionId é obrigatório");
      }
      const { getSpecialMissionStatistics } = this.routes;
      const data = await this.get(getSpecialMissionStatistics, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista de missões especiais (pesquisa) com paginação
   * @param {Object} params - Parâmetros da requisição
   * @param {number} [params.skip=0] - Registros a pular
   * @param {number} [params.limit=20] - Limite de registros
   * @returns {Promise<Object>} Lista de missões com total
   */
  async getAllSpecialMissions(params = {}) {
    try {
      const { getAllSpecialMissions } = this.routes;
      const data = await this.get(getAllSpecialMissions, {
        skip: params.skip || 0,
        limit: params.limit || 20,
      });
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getAllPreCoupons() {
    try {
      const { generateAllCoupon } = this.routes;
      const data = await this.get(generateAllCoupon);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async statusGenerateCoupons() {
    try {
      const { statusGenerateCoupons } = this.routes;
      const data = await this.get(statusGenerateCoupons);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getModules() {
    try {
      const { getallModules } = this.routes;
      const data = await this.get(getallModules);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getAllLandingPage(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "createdAt";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "createdAt";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }

      const { getLandingPage } = this.routes;
      const data = await this.get(getLandingPage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getNotifications() {
    try {
      const { getNotifications } = this.routes;
      const data = await this.get(getNotifications);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async newCoupon(params) {
    try {
      const { newCoupon } = this.routes;
      const data = await this.post(newCoupon, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newRakutenCoupon(params) {
    try {
      const { sendPreCouponRekuten } = this.routes;
      const data = await this.post(sendPreCouponRekuten, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newRewards(params) {
    try {
      const { newRewards } = this.routes;
      const data = await this.post(newRewards, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newMission(params) {
    try {
      const { newMission } = this.routes;
      const data = await this.post(newMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newModule(params) {
    try {
      const { newModule } = this.routes;
      const data = await this.post(newModule, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newusersAdmin(params) {
    try {
      const { newusersAdmin } = this.routes;
      const data = await this.post(newusersAdmin, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newLandingPage(params) {
    try {
      const { newLandingPage } = this.routes;
      const data = await this.post(newLandingPage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageRewards(params) {
    try {
      const { uploadImageRewards } = this.routes;
      const data = await this.post(uploadImageRewards, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageRewardsIcon(params) {
    try {
      const { uploadImageRewardsIcon } = this.routes;
      const data = await this.post(uploadImageRewardsIcon, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageCoverRewards(params) {
    try {
      const { uploadImageCoverRewards } = this.routes;
      const data = await this.post(uploadImageCoverRewards, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageProduct(params) {
    try {
      const { uploadImageProduct } = this.routes;
      const data = await this.post(uploadImageProduct, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageCarousel(params) {
    try {
      const { uploadImageCarousel } = this.routes;
      let mode = params.get("mode");
      params.delete("mode");
      const data = await this.post(
        uploadImageCarousel + "?mode=" + mode,
        params,
      );
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageMissions(params) {
    try {
      const { uploadImageMissions } = this.routes;
      const data = await this.post(uploadImageMissions, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageStore(params) {
    try {
      const { uploadImageStore } = this.routes;
      let mode = params.get("mode");
      params.delete("mode");
      const data = await this.post(uploadImageStore + "?mode=" + mode, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageCoupon(params) {
    try {
      const { uploadImageCoupon } = this.routes;
      const data = await this.post(uploadImageCoupon, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageLandingPage(params) {
    try {
      const { uploadImageLandingPage } = this.routes;
      let mode = params.get("mode");
      params.delete("mode");
      const data = await this.post(
        uploadImageLandingPage + "?mode=" + mode,
        params,
      );
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async generateCoupon(params) {
    try {
      const { generateCoupon } = this.routes;
      const data = await this.post(generateCoupon, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async newCategory(params) {
    try {
      const { newCategory } = this.routes;
      const data = await this.post(newCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newPrize(params) {
    try {
      const { newPrize } = this.routes;
      const data = await this.post(newPrize, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateBannerById(id, params) {
    try {
      const updateBanner = this.routes.updateBanner.replace("{id}", id);
      const data = await this.patch(updateBanner, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newStore(params) {
    try {
      const { newStore } = this.routes;
      const data = await this.post(newStore, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newBanner(params) {
    try {
      const { newBannerImage } = this.routes;
      const data = await this.post(newBannerImage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async orderCategory(params) {
    try {
      const { orderCategory } = this.routes;
      const data = await this.post(orderCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newGroup(params) {
    try {
      const { newGroup } = this.routes;
      const data = await this.post(newGroup, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateGroupById({ id, values }) {
    try {
      const updateGroupById = this.routes.updateGroupById.replace("{id}", id);
      const data = await this.patch(updateGroupById, values);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async orderModule(params) {
    try {
      const { orderModules } = this.routes;
      const data = await this.post(orderModules, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async newAffiliate(params) {
    try {
      const { newAffiliate } = this.routes;
      const data = await this.post(newAffiliate, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getAffiliates(params) {
    try {
      const { getAffiliates } = this.routes;
      const data = await this.get(getAffiliates);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateAffiliate(id, values) {
    try {
      const updateAffiliate = this.routes.updateAffiliate.replace("{id}", id);
      const data = await this.patch(updateAffiliate, values);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setAffiliateActive(id) {
    try {
      const affiliateActive = this.routes.affiliateActive.replace("{id}", id);
      const data = await this.patch(affiliateActive);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getGiftCard() {
    try {
      const { getGiftCard } = this.routes;
      const data = await this.get(getGiftCard);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async generateGiftCard(params) {
    try {
      const { generateGiftCard } = this.routes;
      const data = await this.post(generateGiftCard, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateSettings(params) {
    try {
      const updateSetting = this.routes.updateSetting;
      const data = await this.post(updateSetting, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getHotOffers() {
    try {
      const { listHotOffers } = this.routes;
      const data = await this.get(listHotOffers);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newHotOffers(params) {
    try {
      const { createHotOffers } = this.routes;
      const data = await this.post(createHotOffers, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updatenHotOffersById(id, params) {
    try {
      const updateHotOffers = this.routes.updateHotOffers.replace("{id}", id);
      const data = await this.patch(updateHotOffers, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getPostback() {
    try {
      const { getPostback } = this.routes;
      const data = await this.get(getPostback);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newPostback(params) {
    try {
      const { createPostback } = this.routes;
      const data = await this.post(createPostback, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updatePostBackById(id, params) {
    try {
      const updatePostBack = this.routes.updatePostBack.replace("{id}", id);
      const data = await this.patch(updatePostBack, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /* Quiz / Pesquisa Methods */
  async getQuizzes(params) {
    try {
      const { getQuizzes } = this.routes;
      const data = await this.get(getQuizzes, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async newQuiz(params) {
    try {
      const { newQuiz } = this.routes;
      const data = await this.post(newQuiz, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateQuizById(id, params) {
    try {
      const updateQuiz = this.routes.updateQuiz.replace("{id}", id);
      const data = await this.patch(updateQuiz, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getQuizById(id) {
    try {
      const getQuizById = this.routes.getQuizById.replace("{id}", id);
      const data = await this.get(getQuizById);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async uploadSurveyImage(params) {
    try {
      const { uploadSurveyImage } = this.routes;
      const data = await this.post(uploadSurveyImage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async deleteQuizById(id) {
    try {
      const deleteQuiz = this.routes.deleteQuiz.replace("{id}", id);
      const data = await this.patch(deleteQuiz);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /* Quiz Question Methods */
  async addQuizQuestion(params) {
    try {
      const { addQuizQuestion } = this.routes;
      const { data } = await this.post(addQuizQuestion, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async addQuizQuestionWithOptions(params) {
    try {
      const { addQuizQuestionWithOptions } = this.routes;
      const { data } = await this.post(addQuizQuestionWithOptions, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async updateQuizQuestion(id, params) {
    try {
      const updateQuizQuestion = this.routes.updateQuizQuestion.replace(
        "{id}",
        id,
      );
      const data = await this.patch(updateQuizQuestion, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async deleteQuizQuestion(id) {
    try {
      const deleteQuizQuestion = this.routes.deleteQuizQuestion.replace(
        "{id}",
        id,
      );
      const data = await this.delete(deleteQuizQuestion);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getSettings() {
    try {
      const { getSettings } = this.routes;
      const data = await this.get(getSettings);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateStartCron(params) {
    try {
      const { updateSetting } = this.routes;
      const data = await this.post(updateSetting, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  /* categoria review */
  async newReviewCategory(params) {
    try {
      const { newReviewCategory } = this.routes;
      const data = await this.post(newReviewCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newQuestionsByCategory(params) {
    try {
      const { newQuestionsByCategory } = this.routes;
      const data = await this.post(newQuestionsByCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getAllReviewCategory(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }
      if (params.q === "") {
        params.q = undefined;
      }
      const { getAllReviewCategory } = this.routes;
      const data = await this.get(getAllReviewCategory, params);
      return data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        return error.response;
      }
    }
  }
  async getAllReviewCategoryList() {
    try {
      const { getAllReviewCategoryList } = this.routes;
      const data = await this.get(getAllReviewCategoryList);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateQuestionsByCategory(id, params) {
    try {
      const setQuestionsByCategory = this.routes.setQuestionsByCategory.replace(
        "{id}",
        id,
      );
      const data = await this.patch(setQuestionsByCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateQuestionByQuestionId(id, params) {
    try {
      const setQuestionByQuestionId =
        this.routes.setQuestionByQuestionId.replace("{id}", id);
      const data = await this.patch(setQuestionByQuestionId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateReviewCategory(id, params) {
    try {
      const setReviewCategory = this.routes.setReviewCategory.replace(
        "{id}",
        id,
      );
      const data = await this.patch(setReviewCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async deleteQuestionReviewCategory(id, params) {
    try {
      const deleteQuestionReviewCategory =
        this.routes.deleteQuestionReviewCategory.replace("{id}", id);
      const data = await this.delete(deleteQuestionReviewCategory);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async deleteAllQuestionReviewCategoryId(params) {
    try {
      const { deleteQuestionReviewCategoryId } = this.routes;
      const data = await this.patch(deleteQuestionReviewCategoryId, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async deleteReviewCategory(id, params) {
    try {
      const deleteReviewCategory = this.routes.deleteReviewCategory.replace(
        "{id}",
        id,
      );
      const data = await this.patch(deleteReviewCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadImageCategory(params) {
    try {
      const { uploadImageCategory } = this.routes;
      const data = await this.post(uploadImageCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReviewCategoryById(id) {
    try {
      const getReviewCategoryById = this.routes.getReviewCategoryById.replace(
        "{id}",
        id,
      );
      const data = await this.get(getReviewCategoryById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getQuestionsByCategory(id) {
    try {
      const getQuestionsByCategory = this.routes.getQuestionsByCategory.replace(
        "{id}",
        id,
      );
      const data = await this.get(getQuestionsByCategory);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async findProductsMLList(params) {
    try {
      const { findProductsMLList } = this.routes;
      const data = await this.get(findProductsMLList, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum Produto encontrado");
    } catch (error) {
      throw error;
    }
  }
  async getProductsList(params, signal) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }
      if (params.q === "") {
        params.q = undefined;
      }
      const { getProductsList } = this.routes;

      // Usa o método com cancelamento específico para produtos
      const data = await this.getWithCancelByRoute(
        getProductsList,
        params,
        "produtos", // chave única para identificar requisições de produtos
      );

      return data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        return error.response;
      }
    }
  }
  async newProductReview(params) {
    try {
      const { newProductReview } = this.routes;
      const data = await this.post(newProductReview, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateProductReview(id, params) {
    try {
      const setProductReview = this.routes.setProductReview.replace("{id}", id);
      const data = await this.patch(setProductReview, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getProductById(id) {
    try {
      const getProductById = this.routes.getProductById.replace("{id}", id);
      const data = await this.get(getProductById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async uploadProductImage(params) {
    try {
      const { uploadProductImage } = this.routes;
      const data = await this.post(uploadProductImage, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async findAllBrand(params) {
    try {
      const { listBrand } = this.routes;
      const data = await this.get(listBrand, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReviewList(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }
      if (params.q === "") {
        params.q = undefined;
      }
      if (params.brandId === "") {
        params.brandId = undefined;
      }
      if (params.categoryId === "") {
        params.categoryId = undefined;
      }

      const { getReviewList } = this.routes;
      return await this.getWithCancelByRoute(
        getReviewList,
        params,
        "avaliacao",
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        return error.response;
      }
    }
  }
  async getReviewById(id) {
    try {
      const getReviewById = this.routes.getReviewById.replace("{id}", id);
      const data = await this.get(getReviewById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setReviewById(id, params) {
    try {
      const setReviewById = this.routes.setReviewById.replace("{id}", id);
      const data = await this.patch(setReviewById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getAllBrandReview(params) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.dir === undefined) {
        params.dir = "desc";
      }
      if (params.q === "") {
        params.q = undefined;
      }

      const { getAllBrandReview } = this.routes;
      return await this.getWithCancelByRoute(
        getAllBrandReview,
        params,
        "marca",
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        return error.response;
      }
    }
  }
  async newBrandReview(params) {
    try {
      const { newBrandReview } = this.routes;
      const data = await this.post(newBrandReview, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setBrandReview(id, params) {
    try {
      const setBrandReview = this.routes.setBrandReview.replace("{id}", id);
      const data = await this.patch(setBrandReview, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getBrandById(id) {
    try {
      const getBrandById = this.routes.getBrandById.replace("{id}", id);
      const data = await this.get(getBrandById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getAllApprovalReceipt(params, { signal }) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.order === undefined) {
        params.order = "desc";
      }
      const { getAllApprovalReceipt } = this.routes;
      const data = await this.get(getAllApprovalReceipt, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getImageReceiptCoupon(id) {
    try {
      const getImageReceiptCoupon = this.routes.getImageReceiptCoupon.replace(
        "{id}",
        id,
      );
      const data = await this.get(getImageReceiptCoupon);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getReceiptDetailsById(id) {
    try {
      const getReceiptDetailsById = this.routes.getReceiptDetailsById.replace(
        "{id}",
        id,
      );
      const data = await this.get(getReceiptDetailsById);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setReceiptById(id, params) {
    try {
      const setReceiptById = this.routes.setReceiptById.replace("{id}", id);
      const data = await this.patch(setReceiptById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getAllApprovalMission(params, { signal }) {
    try {
      if (params.sort === undefined && params.dir === undefined) {
        params.sort = "id";
        params.dir = "desc";
      }
      if (params.sort === undefined) {
        params.sort = "id";
      }
      if (params.order === undefined) {
        params.order = "desc";
      }
      if (params.user === undefined) {
        params.user = "";
      }
      if (params.mission === undefined) {
        params.mission = "";
      }

      const { getAllApprovalMission } = this.routes;
      const data = await this.get(getAllApprovalMission, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async setApprovalMissionById(id, params) {
    try {
      const setApprovalMissionById = this.routes.setApprovalMissionById.replace(
        "{id}",
        id,
      );
      const data = await this.patch(setApprovalMissionById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async setApprovalMissionStepById(id, stepId, params) {
    try {
      const url = this.routes.setApprovalMissionStepById
        .replace("{id}", id)
        .replace("{stepId}", stepId);
      const data = await this.patch(url, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async newMissionCategory(params) {
    try {
      const { newMissionCategory } = this.routes;
      const data = await this.post(newMissionCategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getMissionCategory(params) {
    try {
      const { listMissionCategory } = this.routes;
      const data = await this.get(listMissionCategory, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhuma Categoria encontrada");
    } catch (error) {
      throw error;
    }
  }
  async getMissionCategoryById(id) {
    try {
      const { getMissionCategoryById } = this.routes;
      const route = getMissionCategoryById.replace("{id}", id);
      const data = await this.get(route);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateMissionCategoryById(id, params) {
    try {
      const { updateMissionCategory } = this.routes;
      const route = updateMissionCategory.replace("{id}", id);
      const data = await this.patch(route, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newMissionSubcategory(params) {
    try {
      const { newMissionSubcategory } = this.routes;
      const data = await this.post(newMissionSubcategory, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getMissionSubcategory(params) {
    try {
      const { listMissionSubcategory } = this.routes;
      const data = await this.get(listMissionSubcategory, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhuma Subcategoria encontrada");
    } catch (error) {
      throw error;
    }
  }
  async getMissionSubcategoryById(id) {
    try {
      const { getMissionSubcategoryById } = this.routes;
      const route = getMissionSubcategoryById.replace("{id}", id);
      const data = await this.get(route);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async updateMissionSubcategoryById(id, params) {
    try {
      const { updateMissionSubcategory } = this.routes;
      const route = updateMissionSubcategory.replace("{id}", id);
      const data = await this.patch(route, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newRouletteItem(params) {
    try {
      const { newRouletteItem } = this.routes;
      const data = await this.post(newRouletteItem, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setRouletteItemById(id, params) {
    try {
      const setRouletteItemById = this.routes.setRouletteItemById.replace(
        "{id}",
        id,
      );
      const data = await this.patch(setRouletteItemById, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async deleteRoulleteItem(id, params) {
    try {
      const deleteRoulleteItem = this.routes.deleteRoulleteItem.replace(
        "{id}",
        id,
      );
      const data = await this.delete(deleteRoulleteItem, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async getAllRouletteItems(params) {
    try {
      const { getAllRouletteItems } = this.routes;
      const data = await this.get(getAllRouletteItems, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum item encontrado");
    } catch (error) {
      throw error;
    }
  }

  async getSettingsUserPremium(params) {
    try {
      const { getSettingsUserPremium } = this.routes;
      const data = await this.get(getSettingsUserPremium, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum item encontrado");
    } catch (error) {
      throw error;
    }
  }
  async setSettingsUserPremium(params) {
    try {
      const setSettingsUserPremium = this.routes.setSettingsUserPremium;
      const data = await this.patch(setSettingsUserPremium, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getPremiumRewards() {
    try {
      const { getPremiumRewards } = this.routes;
      const data = await this.get(getPremiumRewards);
      if (data?.status === 200) return data;
      else throw Error("Nenhuma recompensa encontrada");
    } catch (error) {
      throw error;
    }
  }

  async setPremiumRewards(params) {
    try {
      const { setPremiumRewards } = this.routes;
      const data = await this.patch(setPremiumRewards, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  async getRoulettePackageSettings(params) {
    try {
      const { getRoulettePackageSettings } = this.routes;
      const data = await this.get(getRoulettePackageSettings, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum item encontrado");
    } catch (error) {
      throw error;
    }
  }
  async setRoulettePackageSettings(params) {
    try {
      const setRoulettePackageSettings = this.routes.setRoulettePackageSettings;
      const data = await this.patch(setRoulettePackageSettings, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async ReportsRoulette(params) {
    try {
      const { reportsRoulette } = this.routes;
      const data = await this.get(reportsRoulette, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum item encontrado");
    } catch (error) {
      return error;
    }
  }

  async getAllCode(params) {
    try {
      const { getAllCodeList } = this.routes;
      const data = await this.get(getAllCodeList, params);
      if (data?.status === 200) return data;
      else throw Error("Nenhum item encontrado");
    } catch (error) {
      throw error;
    }
  }
  async setRedeemCode(id, params) {
    try {
      const setRedeemCode = this.routes.setRedeemCode.replace("{id}", id);
      const data = await this.patch(setRedeemCode, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async newRedeemCode(params) {
    try {
      const { newRedeemCode } = this.routes;
      const data = await this.post(newRedeemCode, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }
  async setActiveRedeemCode(id, params) {
    try {
      const activeRedeemCode = this.routes.activeRedeemCode.replace("{id}", id);
      const data = await this.patch(activeRedeemCode, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém lista de lojas turbinadas
   * @param {Object} params - Parâmetros de busca
   * @returns {Promise<Object>} Lista de lojas turbinadas
   */
  async getBoostedStores(params) {
    try {
      const { getBoostedStores } = this.routes;
      const data = await this.get(getBoostedStores, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Cria uma nova loja turbinada
   * @param {Object} params - Dados da loja turbinada
   * @returns {Promise<Object>} Dados da loja turbinada criada
   */
  async createBoostedStore(params) {
    try {
      const { createBoostedStore } = this.routes;
      const data = await this.post(createBoostedStore, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Atualiza uma loja turbinada
   * @param {number} id - ID da loja turbinada
   * @param {Object} params - Dados para atualização
   * @returns {Promise<Object>} Dados da loja turbinada atualizada
   */
  async updateBoostedStore(id, params) {
    try {
      const updateBoostedStore = this.routes.updateBoostedStore.replace(
        "{id}",
        id,
      );
      const data = await this.patch(updateBoostedStore, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Atualiza questões e opções de uma pesquisa de forma incremental (conciliação)
   * @param {number|string} surveyId - ID da pesquisa
   * @param {Object} params - Objeto contendo { questions: [...] }
   * @returns {Promise<Object>} Resposta da API
   */
  async updateQuizQuestionWithOptions(surveyId, params) {
    try {
      const route = this.routes.updateQuizQuestionWithOptions.replace(
        "{surveyId}",
        surveyId,
      );
      const { data } = await this.patch(route, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém relatório de indicações
   * @param {Object} params - Parâmetros da consulta
   * @param {number} [params.startDate] - Data de início (timestamp)
   * @param {number} [params.endDate] - Data de fim (timestamp)
   * @param {string} [params.rankingFilter] - Filtro do ranking
   * @returns {Promise<Object>} Dados do relatório
   */
  async getReportsReferral(params) {
    try {
      const { getReportsReferral } = this.routes;
      const data = await this.get(getReportsReferral, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Atualiza a ordem de uma pesquisa
   * @param {number|string} surveyId - ID da pesquisa
   * @param {Object} params - Objeto contendo { orderSurvey: "nova_ordem" }
   * @returns {Promise<Object>} Resposta da API
   */
  async orderSurvey(surveyId, params) {
    try {
      const route = this.routes.orderSurvey.replace("{id}", surveyId);
      const { data } = await this.patch(route, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém detalhes dos indicados de um usuário
   * @param {number} id - ID do usuário indicador
   * @param {Object} params - Parâmetros de paginação/ordenação
   * @returns {Promise<Object>} Lista de indicados
   */
  async getReportsReferralDetail(id, params) {
    try {
      const url = this.routes.getReportsReferralDetail.replace("{id}", id);
      const data = await this.get(url, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém todas as partidas de futebol
   * @returns {Promise<Object>} Lista de partidas
   */
  async getAllMatches(params = {}) {
    try {
      const { getAllMatches } = this.routes;
      const response = await this.get(getAllMatches, params);
      const matches = Array.isArray(response?.data) ? response.data : [];
      // Mapeia para o formato esperado pelo AsyncAutocomplete (id + name)
      return {
        data: matches.map((match) => ({
          id: match.id,
          name: `${match.homeTeam?.name || "?"} vs ${match.awayTeam?.name || "?"}`,
        })),
      };
    } catch (error) {
      return { data: [] };
    }
  }

  /**
   * Obtém todas as cartelas vencedoras pendentes de aprovação
   * @param {Object} params - Parâmetros de paginação e ordenação
   * @param {Object} options - Opções adicionais (signal)
   * @returns {Promise<Object>} Lista de cartelas vencedoras
   */
  async getWinnersPending(params, { signal } = {}) {
    try {
      if (!params.sort) params.sort = "createdAt";
      if (!params.dir) params.dir = "desc";

      const { getWinnersPending } = this.routes;
      const data = await this.get(getWinnersPending, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém detalhes de uma cartela específica
   * @param {string|number} id - ID da cartela
   * @returns {Promise<Object>} Dados da cartela
   */
  async getGameCardById(id) {
    try {
      const url = this.routes.getGameCardById.replace("{id}", id);
      const data = await this.get(url);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Aprova ou reprova o pagamento de PIX de uma cartela vencedora
   * @param {string|number} id - ID da cartela
   * @param {Object} params - { approval: boolean }
   * @returns {Promise<Object>} Resultado da operação
   */
  async approvalGameCard(id, params) {
    try {
      const url = this.routes.approvalGameCard.replace("{id}", id);
      const data = await this.patch(url, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Faz upload de arquivo CSV da Colgate para processamento
   * @param {FormData} formData - FormData contendo o arquivo CSV
   * @returns {Promise<Object>} Resposta com jobId
   */
  async uploadColgateCsv(formData) {
    try {
      const { colgateCsvUpload } = this.routes;
      const data = await this.post(colgateCsvUpload, formData);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Obtém histórico de processamentos CSV da Colgate
   * @returns {Promise<Array>} Lista de processamentos
   */
  async getColgateCsvHistory() {
    try {
      const { colgateCsvHistory } = this.routes;
      const data = await this.get(colgateCsvHistory);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Cancela processamento CSV da Colgate em andamento
   * @param {string} jobId - ID do job a cancelar
   * @returns {Promise<Object>} Confirmação de cancelamento
   */
  async cancelColgateCsvProcess(jobId) {
    try {
      const url = this.routes.colgateCsvCancel.replace("{jobId}", jobId);
      const data = await this.post(url);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Exporta relatório de status de missão para CSV
   * @param {Object} [params] - Parâmetros da requisição
   * @param {boolean} [params.rescued] - Filtro por status de resgate
   * @param {number} [params.startDate] - Data inicial filtro createdAt (timestamp)
   * @param {number} [params.endDate] - Data final filtro createdAt (timestamp)
   * @param {number} [params.updatedAtStart] - Data inicial filtro updatedAt (timestamp)
   * @param {number} [params.updatedAtEnd] - Data final filtro updatedAt (timestamp)
   * @returns {Promise<Object>} URL do arquivo CSV no S3
   */
  async exportMissionStatusReport(params) {
    try {
      const { exportMissionStatusReport } = this.routes;
      const data = await this.post(exportMissionStatusReport, params);
      return data;
    } catch (error) {
      return error.response;
    }
  }

  /**
   * Exporta a base de usuários do Big Torcida
   * @returns {Promise<Object>} Resultado com o link do CSV no S3
   */
  async exportBaseGameCard() {
    try {
      const { exportBaseGameCard } = this.routes;
      const data = await this.get(exportBaseGameCard);
      return data;
    } catch (error) {
      return error.response;
    }
  }
}
const ApiService = new ServiceApi();
export default ApiService;
